// src/components/StudyClosePatientsModal.js
//
// Opens after an admin closes a study (status CLOSED or CLOSED/CLOSING). Lists
// the patients enrolled in it and what will happen to each:
//   - "Moves to Lung Center Referrals": every study they're in is now closed
//     or diagnostics, so they can be considered for other studies.
//   - "Stays Enrolled": they're still in another open study. The closed study
//     is recorded on their history either way.
// Nothing changes until the admin clicks Confirm. Skipping leaves the study
// closed; the study page keeps offering the same list until it's done.

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRightLeft, CheckCircle2, Loader2, AlertTriangle, X, Lock } from "lucide-react";
import { loadStudyClosePlans, applyStudyClosePlans } from "../services/studyCloseService";
import { studyDisplayName, REFERRALS_STATUS, RETURN_REASON_TEXT } from "../services/enrollmentRules";

const StudyClosePatientsModal = ({ open, study, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { plans, allStudies, sheetError }
  const [loadError, setLoadError] = useState("");
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open || !study) return undefined;
    let cancelled = false;
    setData(null);
    setResults(null);
    setLoadError("");
    setLoading(true);
    loadStudyClosePlans(study)
      .then((d) => !cancelled && setData(d))
      .catch((err) => {
        console.error("Study close: couldn't load patients", err);
        if (!cancelled) setLoadError("Couldn't load this study's patients. Try again later.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, study]);

  const plans = data?.plans || [];
  const moving = plans.filter((p) => p.move);
  const staying = plans.filter((p) => !p.move);
  const name = studyDisplayName(study);

  const confirm = async () => {
    setApplying(true);
    try {
      setResults(await applyStudyClosePlans(plans, study, data.allStudies));
    } finally {
      setApplying(false);
    }
  };

  const failed = (results || []).filter((r) => !r.ok);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="study-close-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 8 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="enroll-modal-panel relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-teal-400/40 bg-slate-950/95 backdrop-blur-2xl shadow-[0_24px_60px_-12px_rgba(45,212,191,0.45)] text-slate-100"
          >
            <div className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center shrink-0">
                  <Lock className="w-[18px] h-[18px] text-teal-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-white leading-tight">{name} is closed</h3>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                    Patients enrolled in it can go back to {REFERRALS_STATUS} to be considered for other studies. Their
                    enrollment in {name} stays on their record.
                  </p>
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Finding enrolled patients…
                </div>
              )}
              {loadError && <p className="py-4 text-xs text-rose-300">{loadError}</p>}

              {data?.sheetError && (
                <div className="mb-3 flex items-start gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-2.5 py-1.5 text-[11px] text-amber-200">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  The patient sheet couldn't be read, so sheet patients may be missing. You can come back to this from
                  the study page.
                </div>
              )}

              {data && !results && plans.length === 0 && (
                <p className="py-4 text-xs text-slate-400">
                  No patients currently marked Enrolled are recorded in this study. Nothing to update.
                </p>
              )}

              {data && !results && moving.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-wide text-teal-300 mb-1.5">
                    Moves to {REFERRALS_STATUS} ({moving.length})
                  </p>
                  <ul className="space-y-1">
                    {moving.map((p) => (
                      <li
                        key={`${p.patient.source}-${p.patient.id}`}
                        className="flex items-start gap-2 rounded-lg border border-teal-400/20 bg-teal-400/10 px-2.5 py-1.5 text-xs"
                      >
                        <ArrowRightLeft className="w-3.5 h-3.5 mt-0.5 shrink-0 text-teal-300" />
                        <span>
                          <span className="font-semibold text-white">{p.patient.name || "Unnamed patient"}</span>
                          <span className="text-slate-400"> · {RETURN_REASON_TEXT[p.reason]}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data && !results && staying.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">
                    Stays Enrolled ({staying.length})
                  </p>
                  <ul className="space-y-1">
                    {staying.map((p) => (
                      <li
                        key={`${p.patient.source}-${p.patient.id}`}
                        className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-xs"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-300" />
                        <span>
                          <span className="font-semibold text-white">{p.patient.name || "Unnamed patient"}</span>
                          <span className="text-slate-400">
                            {" "}
                            {p.unsure
                              ? `· their ${p.keeping
                                  .map((e) => `${e.studyName || e.studyId} match (${e.matchScore}%)`)
                                  .join(", ")} needs verifying on the patient page first`
                              : `· still in ${p.keeping.map((e) => e.studyName || e.studyId).join(", ")}`}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results && (
                <div className="mb-3 space-y-1.5 text-xs">
                  <p className="text-emerald-200">
                    Done. {results.filter((r) => r.ok && r.moved).length} moved to {REFERRALS_STATUS},{" "}
                    {results.filter((r) => r.ok && !r.moved).length} updated and kept Enrolled.
                  </p>
                  {results
                    .filter((r) => r.skipped)
                    .map((r) => (
                      <p key={`s-${r.id}`} className="text-slate-400">
                        {r.name}: {r.skipped}
                      </p>
                    ))}
                  {failed.length > 0 && (
                    <div className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-1.5 text-rose-200">
                      {failed.length} couldn't be updated and were left as they were:
                      <ul className="mt-1 list-disc pl-4">
                        {failed.map((r) => (
                          <li key={`f-${r.id}`}>
                            {r.name || r.id}: {r.error}
                          </li>
                        ))}
                      </ul>
                      You can try again from the study page.
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                {!results && plans.length > 0 && (
                  <button
                    type="button"
                    disabled={applying || loading}
                    onClick={confirm}
                    className="enroll-solid-btn flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 ring-1 ring-white/20 text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-50"
                  >
                    {applying && <Loader2 className="w-4 h-4 animate-spin" />}
                    Confirm
                  </button>
                )}
                <button
                  type="button"
                  disabled={applying}
                  onClick={onClose}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/15 text-sm font-semibold text-slate-100 hover:bg-white/[0.1] transition-all disabled:opacity-50"
                >
                  {results || plans.length === 0 ? "Done" : "Not now"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StudyClosePatientsModal;
